import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import {
  CourseMaterial,
  CourseMaterialCategoryLabel,
  MaterialStatus,
  UpdateCourseMaterialDto,
  materialStatus,
} from '../../../core/interfaces/course-material';
import { AdminCourseMaterialService } from '../../../core/services/admin-course-material.service';
import {
  AcademicSchedulingOptions,
  SchedulingSubject,
} from '../../../core/interfaces/academic-appointment';
import { CourseMaterialCardComponent } from '../../../components/ui/views/course-material-card/course-material-card.component';
import { MaterialPreviewComponent } from '../../../components/ui/views/material-preview/material-preview.component';
import { ErrorStateComponent } from '../../../components/ui/error-state/error-state.component';
import { EmptyStateComponent } from '../../../components/ui/empty-state/empty-state.component';
import { AuthService } from '../../../core/services/utils/auth.service';
import { ExternalNavigationService } from '../../../core/services/utils/external-navigation.service';
import { matchesSearch } from '../../../core/utils/search-text';
import {
  SubjectChoiceService,
  SubjectPathGroup,
} from '../../../core/services/utils/subject-choice.service';

interface CategoryFilter {
  key: 'ALL' | CourseMaterialCategoryLabel;
  i18nKey: string;
  icon: string;
}

const CATEGORY_FILTERS: CategoryFilter[] = [
  { key: 'ALL',      i18nKey: 'LIBRARY.FILTER_ALL',         icon: 'fa-solid fa-grip' },
  { key: 'Pdf',      i18nKey: 'LIBRARY.CATEGORY.Pdf',       icon: 'fa-solid fa-file-pdf' },
  { key: 'Video',    i18nKey: 'LIBRARY.CATEGORY.Video',     icon: 'fa-solid fa-circle-play' },
  { key: 'Image',    i18nKey: 'LIBRARY.CATEGORY.Image',     icon: 'fa-solid fa-image' },
  { key: 'Slide',    i18nKey: 'LIBRARY.CATEGORY.Slide',     icon: 'fa-solid fa-file-powerpoint' },
  { key: 'Document', i18nKey: 'LIBRARY.CATEGORY.Document',  icon: 'fa-solid fa-file-lines' },
  { key: 'Audio',    i18nKey: 'LIBRARY.CATEGORY.Audio',     icon: 'fa-solid fa-headphones' },
  { key: 'Other',    i18nKey: 'LIBRARY.CATEGORY.Other',     icon: 'fa-solid fa-paperclip' },
];

/**
 * Bibliothèque de l'établissement.
 *
 * L'écran chargeait auparavant le vide tant qu'on n'avait pas tapé un
 * identifiant numérique à la main. Il affiche désormais toute la bibliothèque
 * d'emblée, et les filtres sont des listes alimentées par le référentiel.
 */
@Component({
  selector: 'app-admin-library',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CourseMaterialCardComponent,
    MaterialPreviewComponent,
    ErrorStateComponent,
    EmptyStateComponent,
  ],
  templateUrl: './admin-library.component.html',
  styleUrl: './admin-library.component.css',
})
export class AdminLibraryComponent implements OnInit {
  private readonly service = inject(AdminCourseMaterialService);
  private readonly subjectChoice = inject(SubjectChoiceService);
  private readonly navigation = inject(ExternalNavigationService);

  /**
   * Droit d'écrire dans la bibliothèque.
   *
   * <p>L'écran est ouvert en lecture à qui porte `library.read.all` —
   * l'inspecteur, notamment. Les verbes d'écriture demandent davantage, et le
   * back le vérifie : ce test ne protège rien, il évite d'offrir un bouton dont
   * la réponse sera 403.</p>
   */
  readonly canWrite = inject(AuthService).hasPermission('library.write.own');

  readonly categoryFilters = CATEGORY_FILTERS;

  readonly options = signal<AcademicSchedulingOptions>({
    subjects: [], groups: [], students: [], professors: [],
  });

  // ─── Liste ──────────────────────────────────────────────────
  readonly materials = signal<CourseMaterial[]>([]);
  readonly loading = signal(false);
  readonly errored = signal(false);

  readonly selectedCategory = signal<CategoryFilter['key']>('ALL');
  readonly query = signal('');

  // Filtres serveur — `null` signifie « tout », l'état par défaut.
  filterProfessorId: number | null = null;
  filterSubjectId: number | null = null;
  filterGroupId: number | null = null;
  /**
   * Les archives sont ramenees systematiquement.
   *
   * <p>Le tri entre publie, en attente et archive se fait ensuite en local :
   * demander au serveur de les exclure interdirait de basculer sur « archive »
   * sans un aller-retour, et laisserait la facette mentir le temps du
   * chargement.</p>
   */
  includeArchived = true;

  /** Facette de statut. `ALL` est l'etat par defaut, comme partout ailleurs. */
  readonly statusFilter = signal<'ALL' | MaterialStatus>('ALL');

  /**
   * Des filtres sont posés. Un accesseur, pas un `computed` : trois des quatre
   * champs sont liés par ngModel, sans producteur — un `computed` ne se
   * recalculait qu'au changement de statut, et le bouton « Effacer » restait caché.
   */
  /**
   * Un filtre est-il posé — où qu'il vive ? Les listes du panneau, le statut,
   * mais aussi la puce de catégorie et la recherche : les oublier laissait
   * l'écran sans bouton d'effacement alors qu'il ne montrait qu'une partie de
   * la bibliothèque, et le bouton, quand il était là, n'effaçait qu'à moitié.
   */
  get hasFilters(): boolean {
    return this.filterProfessorId !== null
       || this.filterSubjectId !== null
       || this.filterGroupId !== null
       || this.statusFilter() !== 'ALL'
       || this.selectedCategory() !== 'ALL'
       || this.query().trim().length > 0;
  }

  /** Filtrage local : catégorie et recherche libre, sans aller-retour serveur. */
  readonly filtered = computed(() => {
    const q = this.query();
    const cat = this.selectedCategory();
    const status = this.statusFilter();

    return this.materials().filter((m) => {
      if (status !== 'ALL' && materialStatus(m) !== status) return false;
      if (cat !== 'ALL' && m.category !== cat) return false;
      return matchesSearch(q, m.title, m.description, m.subjectName, m.themeTitle, m.professorFullName);
    });
  });

  // ─── Modales ────────────────────────────────────────────────
  readonly editOpen = signal(false);
  readonly saving = signal(false);
  editTarget: CourseMaterial | null = null;
  editForm: UpdateCourseMaterialDto = {};

  readonly deleteTarget = signal<CourseMaterial | null>(null);
  readonly deleting = signal(false);

  // ─── Aperçu ─────────────────────────────────────────────────
  readonly previewTarget = signal<CourseMaterial | null>(null);

  /** Le chemin de téléchargement dépend du rôle : ici celui de l'administration. */
  readonly downloadFn = (id: number) => this.service.download(id);

  ngOnInit(): void {
    this.loadOptions();
    this.load();
  }

  setCategory(key: CategoryFilter['key']): void { this.selectedCategory.set(key); }

  setStatus(value: 'ALL' | MaterialStatus): void { this.statusFilter.set(value); }
  onQueryInput(v: string): void { this.query.set(v); }

  applyFilters(): void { this.load(); }

  clearFilters(): void {
    this.filterProfessorId = null;
    this.filterSubjectId = null;
    this.filterGroupId = null;
    this.statusFilter.set('ALL');
    this.selectedCategory.set('ALL');
    this.query.set('');
    this.load();
  }

  reload(): void { this.load(); }

  private loadOptions(): void {
    this.service.options().subscribe({
      next: (res) => {
        if (res?.data) this.options.set(res.data);
      },
      error: () => {
        // Sans référentiel, la bibliothèque reste consultable : seuls les
        // filtres perdent leurs listes.
        this.options.set({ subjects: [], groups: [], students: [], professors: [] });
      },
    });
  }

  /**
   * Remet un support archive en circulation.
   *
   * <p>Sans retour possible, l'archivage devenait une suppression deguisee :
   * une piece rangee par erreur ne se recuperait qu'en base.</p>
   */
  unarchive(m: CourseMaterial): void {
    this.service.unarchive(m.id).subscribe({
      next: () => this.reload(),
      // L'intercepteur global signale deja l'echec reseau ou serveur.
      error: () => undefined,
    });
  }

  /** Requête de liste en vol — une seule à la fois. */
  private listRequest?: Subscription;

  private load(): void {
    // Deux listes changées coup sur coup lançaient deux recherches : la plus
    // lente gagnait, et la liste ne correspondait plus aux filtres affichés.
    this.listRequest?.unsubscribe();
    this.loading.set(true);
    this.errored.set(false);

    this.listRequest = this.service
      .search({
        professorId: this.filterProfessorId,
        subjectId: this.filterSubjectId,
        groupId: this.filterGroupId,
        includeArchived: this.includeArchived,
      })
      .subscribe({
        next: (res) => {
          this.materials.set(res?.data ?? []);
          this.loading.set(false);
        },
        error: () => {
          // Une liste vide reste vide : aucun support de démonstration.
          this.materials.set([]);
          this.errored.set(true);
          this.loading.set(false);
        },
      });
  }

  // ── Édition ─────────────────────────────────────────────────
  openEdit(m: CourseMaterial): void {
    this.editTarget = m;
    this.editForm = { title: m.title, description: m.description ?? '', isPublic: m.isPublic };
    this.editOpen.set(true);
  }
  closeEdit(): void { this.editOpen.set(false); this.editTarget = null; }

  submitEdit(): void {
    if (!this.editTarget) return;
    this.saving.set(true);
    this.service.updateMeta(this.editTarget.id, this.editForm).subscribe({
      next: (res) => {
        if (res?.data)
          this.materials.update((list) => list.map((m) => (m.id === this.editTarget!.id ? res.data! : m)));
        this.saving.set(false);
        this.closeEdit();
      },
      error: () => this.saving.set(false),
    });
  }

  // ── Archivage ───────────────────────────────────────────────
  openDelete(m: CourseMaterial): void { this.deleteTarget.set(m); }
  cancelDelete(): void { this.deleteTarget.set(null); }

  confirmDelete(): void {
    const m = this.deleteTarget();
    if (!m) return;
    this.deleting.set(true);
    this.service.delete(m.id).subscribe({
      next: () => {
        this.materials.update((list) => list.filter((x) => x.id !== m.id));
        this.deleting.set(false);
        this.deleteTarget.set(null);
      },
      error: () => this.deleting.set(false),
    });
  }

  // ── Téléchargement ──────────────────────────────────────────
  /**
   * Telecharge un support.
   *
   * <p>Par l'URL presignee quand elle existe : le fichier part du stockage
   * directement au navigateur, qui l'ecrit au fur et a mesure. L'API n'a plus
   * a relayer le flux — une video de 1,5 Go y mourait deux fois, le serveur
   * tenant la requete ouverte pour rien et le navigateur tentant de bufferiser
   * le tout en memoire (`net::ERR_FAILED` apres une vingtaine de secondes).</p>
   *
   * <p>Le nom d'origine est deja force par la signature
   * (`Content-Disposition`), la cle etant opaque.</p>
   *
   * <p>Le relais par l'API reste le repli, pour les seuls supports herites
   * encore ranges sur le disque : eux n'ont pas de cle a signer.</p>
   */
  onDownload(m: CourseMaterial): void {
    if (m.previewUrl) {
      window.location.href = m.previewUrl;
      return;
    }

    this.service.download(m.id).subscribe({
      next: (res) => {
        const blob = res.body;
        if (!blob) return;
        const fn = this.extractFilename(res.headers.get('Content-Disposition'))
          ?? m.fileName ?? `${m.title}${m.extension ?? ''}`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fn;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
    });
  }

  onOpenExternal(m: CourseMaterial): void {
    this.navigation.open(m.externalUrl);
  }

  private extractFilename(header: string | null): string | null {
    if (!header) return null;
    const m = /filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)["']?/i.exec(header);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /**
   * Libellé d'une matière du filtre.
   *
   * <p>Le référentiel de l'administration couvre tout l'établissement : le même
   * nom de matière y revient une fois par parcours. Sans le parcours, le filtre
   * propose des entrées identiques dont une seule est la bonne.</p>
   */
  subjectLabel(s: SchedulingSubject): string {
    return this.subjectChoice.label(s, this.options().subjects);
  }

  /** Matières du filtre, regroupées par parcours. */
  readonly subjectGroups = computed<SubjectPathGroup[]>(() =>
    this.subjectChoice.groupByPath(this.options().subjects),
  );
}
